import {
  Apartment,
  Email,
  Home,
  LocalHospital,
  LocationCity,
  Lock,
  Phone,
} from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Grid, Paper, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../common/button/commonButton";
import MessageDialog from "../../common/dialog/commonDialogBox";
import { APP_LINK } from "../../common/menu/menuItems/menuList";
import ReusableFormFields from "../../common/textfield/ReusableTextfield";
import { styles } from "../../commonStyle/formStyles";
import { addOrganization, resetCurrentOrganization } from "../../redux/organization/organizationAction";

const AddOrganization = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [messageDialog, setMessageDialog] = useState({
        open: false,
        title: "Notification",
        message: "",
        type: "success",
    });

    const orgState = useSelector((state) => state.organization?.addOrganization || {});

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        control,
        formState: { errors },
    } = useForm({
        defaultValues: {
            org_name: "",
            hospital_name: "",
            email: "",
            phone: "",
            city: "",
            password: "",
            address: ""
        }
    });

    useEffect(() => {
        if (orgState.success) {
            setMessageDialog({
                open: true,
                title: "Organization Registration",
                message: "Organization added successfully!",
                type: "success",
            });
            reset();
            dispatch(resetCurrentOrganization());
            setTimeout(() => {
                navigate(APP_LINK.LISTORGANIZATION);
            }, 1500);
        } else if (orgState.error) {
            setMessageDialog({
                open: true,
                title: "Registration Error",
                message: orgState.error || "An error occurred while adding the organization",
                type: "error",
            });
            dispatch(resetCurrentOrganization());
        }
    }, [orgState, reset, dispatch, navigate]);

    const inputFields = useMemo(() => [
        {
            name: "org_name",
            label: "Organization Name / நிறுவனத்தின் பெயர்",
            placeholder: "Enter organization name",
            type: "text",
            rules: { required: "Organization name is required" },
            startIcon: <Apartment />,
        },
        {
            name: "hospital_name",
            label: "Hospital Name / மருத்துவமனையின் பெயர்",
            placeholder: "Enter hospital name",
            type: "text",
            rules: { required: "Hospital name is required" },
            startIcon: <LocalHospital />,
        },
        {
            name: "email",
            label: "Email Address / மின்னஞ்சல் முகவரி",
            placeholder: "Enter email",
            type: "email",
            rules: { 
                required: "Email is required",
                pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address"
                }
            },
            startIcon: <Email />,
        },
        {
            name: "phone",
            label: "Phone Number / தொலைபேசி எண்",
            placeholder: "Enter phone number",
            type: "tel",
            maxLength: 10,
            rules: { 
                required: "Phone number is required",
                pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Invalid phone number (10 digits required)"
                }
            },
            startIcon: <Phone />,
        },
        {
            name: "city",
            label: "City / மாநகரம்",
            placeholder: "Enter city",
            type: "text",
            rules: { required: "City is required" },
            startIcon: <LocationCity />,
        },
        {
            name: "password",
            label: "Password / கடவுச்சொல்",
            placeholder: "Enter password",
            type: "password",
            rules: { required: "Password is required" },
            startIcon: <Lock />,
        },
        {
            name: "address",
            label: "Address / முகவரி",
            placeholder: "Enter address",
            type: "text",
            multipleLine: true,
            rules: { required: "Address is required" },
            startIcon: <Home />,
        },
    ], []);

    const onSubmit = (data) => {
        dispatch(addOrganization(data));
    };

    const handleBack = () => {
        navigate(APP_LINK.LISTORGANIZATION);
    };

    const handleDialogClose = () => {
        setMessageDialog({ ...messageDialog, open: false });
    };

    return (
        <Box sx={styles.container}>
            <Paper sx={styles.paper} elevation={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={12} md={8} lg={9}>
                        <Typography variant="h3" sx={styles.heading}>
                            Add New Organization
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={12} md={4} lg={3} mb={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={handleBack}
                            startIcon={<ArrowBackIcon />}
                        >
                            Back
                        </Button>
                    </Grid>
                </Grid>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <ReusableFormFields
                                inputFields={inputFields}
                                register={register}
                                errors={errors}
                                setValue={setValue}
                                control={control}
                                gridProps={{ xs: 12, sm: 12, md: 6 }}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={styles.buttonsContainer}>
                        <CustomButton
                            type="button"
                            text="Cancel"
                            variant="contained"
                            disableElevation
                            size="small"
                            onClick={handleBack}
                        />
                        <CustomButton
                            type="submit"
                            text="Save"
                            variant="contained"
                            disableElevation
                            size="small"
                            disabled={orgState.loading}
                            loading={orgState.loading}
                        />
                    </Box>
                </form>
            </Paper>
            <MessageDialog
                open={messageDialog.open}
                onClose={handleDialogClose}
                title={messageDialog.title}
                message={messageDialog.message}
                type={messageDialog.type}
            />
        </Box>
    );
};

export default AddOrganization;
